'use client';
import { useEffect, useState } from 'react';
import { useRewardsHubConfig } from '@/hooks/use-rewards/use-rewards';
import { useRewardsHubAnalyticsHandlers } from '@/hooks/use-rewards/use-rewards-hub-analytics-handlers';
import { useMdsInstance } from '@/hooks/use-mds-instance';
import type { RewardsPageConfig } from '@/logic/page-config/rewards/rewards-page-config';
import { GenerositySwimlaneContainer, type GenerosityWidgetId } from '@fanduel/rewards-hub-client';
import { useStore } from '@/store/store';
import { RewardMachineBanner } from '../reward-machine/reward-machine-banner/reward-machine-banner.component';
import { REWARDS_MODULE } from '@/tracking/amplitude/events/rewards-hub/event-names';

export const GenerositySwimlane = ({
    features,
    isLoggedIn,
    pageConfig,
    rmData
}: {
    features: Record<GenerosityWidgetId, boolean>;
    isLoggedIn: boolean;
    pageConfig: RewardsPageConfig;
    rmData?: any; // Rewards Machine Banner does not have proper types
}) => {
    const rewardsConfig = useRewardsHubConfig(pageConfig, isLoggedIn);
    const { handlers, promoHandlers } = useRewardsHubAnalyticsHandlers(isLoggedIn, {
        surface: 'merch-swimlane',
        module: REWARDS_MODULE.GENEROSITY_SWIMLANE
    });
    const userId = useStore((state) => state.userContext.accountSettings.accountID);
    const mdsInstance = useMdsInstance(userId);
    const { entry: entryData } = useStore((state) => state.rewardMachine);
    const isModalOpen = useStore(
        (state) => state.accountHubModals.accountHub.isOpen || state.accountHubModals.deposit.isOpen
    );
    const [isWindowActive, setIsWindowActive] = useState<boolean>(false);
    useEffect(() => {
        const update = () => setIsWindowActive(!document.hidden && document.hasFocus());

        globalThis.addEventListener('focus', update);
        globalThis.addEventListener('blur', update);
        document.addEventListener('visibilitychange', update);

        update();

        return () => {
            globalThis.removeEventListener('focus', update);
            globalThis.removeEventListener('blur', update);
            document.removeEventListener('visibilitychange', update);
        };
    }, []);

    const isAnimationPaused = isModalOpen || !isWindowActive;

    return (
        <GenerositySwimlaneContainer
            rewardMachine={
                rmData && {
                    component: () => <RewardMachineBanner {...rmData} style={{ width: '100%' }} />,
                    entryData: entryData as object // fix types in rewards-hub-client
                }
            }
            config={rewardsConfig}
            features={features}
            handlers={handlers}
            promoHandlers={promoHandlers}
            MDSInstance={mdsInstance}
            isAnimationPaused={isAnimationPaused}
        />
    );
};
